import React, { useState, useEffect, useRef } from 'react';
import { getUploadBase } from '../utils/api';

const EMOJIS = [
  { emoji: '😊', label: 'Happy' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '⚠️', label: 'Alert' },
  { emoji: '🤔', label: 'Confused' },
  { emoji: '😂', label: 'Laugh' }
];

function FormMessage({ onSend, addressee, placeholder = 'Type your message...', initial = '' }) {
    const [message, setMessage] = useState(initial);
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [sending, setSending] = useState(false);
    const [showEmojis, setShowEmojis] = useState(false);
    const [error, setError] = useState(null);
    const emojiMenuRef = useRef(null);

    const MAX_FILES = 5;
    const MAX_TOTAL_SIZE = 15 * 1024 * 1024; // 15 MB

    useEffect(() => {
        if (files.length === 0) {
            setPreviews([]);
            return;
        }
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setPreviews(newPreviews);
        return () => {
            newPreviews.forEach(url => URL.revokeObjectURL(url));
        };
    }, [files]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (emojiMenuRef.current && !emojiMenuRef.current.contains(e.target)) {
                setShowEmojis(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSend = async () => {
        const text = (message || '').trim();
        if (!text && files.length === 0) return;
        setSending(true);
        setError(null);
        
        try {
            const fileUrls = [];
            
            // Upload all files
            if (files.length > 0) {
                for (const file of files) {
                    const form = new FormData();
                    form.append('addressee', addressee || '');
                    form.append('file', file);
                    const token = localStorage.getItem('token') || '';
                    const res = await fetch(`${getUploadBase()}/uploadmessage`, {
                        method: 'POST',
                        headers: { Authorization: token },
                        body: form
                    });
                    if (!res.ok) {
                        const error = await res.json().catch(() => ({}));
                        throw new Error(error.error || 'Upload failed');
                    }
                    const body = await res.json();
                    fileUrls.push(body.url);
                }
            }
            
            // Send single message with all file URLs
            if (typeof onSend === 'function') {
                onSend(text || '', fileUrls.length > 0 ? fileUrls.join('|') : null);
            }

            setMessage('');
            setFiles([]);
            setPreviews([]);
        } catch (err) {
            console.error('Send error', err);
            alert('Failed to send message: ' + (err.message || ''));
        } finally {
            setSending(false);
        }
    };

    const getFileKind = (file) => {
        if (!file) return 'file';
        if (file.type.startsWith('image/')) return 'image';
        if (file.type.startsWith('video/')) return 'video';
        if (file.type.startsWith('audio/')) return 'audio';
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext)) return 'video';
        if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) return 'audio';
        return 'file';
    };

    const handleFileChange = (e) => {
        const newFiles = e.target.files ? Array.from(e.target.files) : [];
        
        // Clear error
        setError(null);
        
        // Check total number of files
        if (files.length + newFiles.length > MAX_FILES) {
            setError(`Maximum ${MAX_FILES} files allowed. You have ${files.length} already.`);
            return;
        }
        
        // Calculate total size
        const currentSize = files.reduce((sum, f) => sum + f.size, 0);
        const newSize = newFiles.reduce((sum, f) => sum + f.size, 0);
        const totalSize = currentSize + newSize;
        
        if (totalSize > MAX_TOTAL_SIZE) {
            const maxMB = (MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(1);
            const currentMB = (currentSize / (1024 * 1024)).toFixed(1);
            const newMB = (newSize / (1024 * 1024)).toFixed(1);
            setError(`Total size exceeds ${maxMB}MB. Current: ${currentMB}MB, New: ${newMB}MB`);
            return;
        }
        
        setFiles(prev => [...prev, ...newFiles]);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const addEmoji = (emoji) => {
        setMessage(prev => prev + emoji);
        setShowEmojis(false);
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    const styles = {
        emojiMenu: {
            position: 'absolute',
            bottom: '100%',
            left: 0,
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 8,
            boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
            padding: '8px',
            display: 'flex',
            gap: '4px',
            marginBottom: 8,
            zIndex: 100
        },
        emojiButton: {
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '4px 8px',
            borderRadius: 4,
            transition: 'all 0.2s'
        }
    };

    return (
        <div className="form-message">
            <textarea
                className="fm-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={2}
                aria-label="Message"
            />

            {error && (
                <div style={{ 
                    marginTop: 8, 
                    padding: 8, 
                    background: '#ffebee', 
                    color: '#d32f2f', 
                    borderRadius: 6,
                    fontSize: 12
                }}>
                    {error}
                </div>
            )}

            {/* File size info */}
            {files.length > 0 && (
                <div style={{ 
                    marginTop: 8, 
                    fontSize: 12, 
                    color: '#666'
                }}>
                    {files.length}/{MAX_FILES} images, {((files.reduce((sum, f) => sum + f.size, 0)) / (1024 * 1024)).toFixed(1)}/{(MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(0)} MB
                </div>
            )}

            {/* Image previews */}
            {previews.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {previews.map((preview, idx) => {
                        const file = files[idx];
                        const kind = getFileKind(file);
                        return (
                            <div key={idx} style={{ position: 'relative', width: 120, minWidth: 120, borderRadius: 10, overflow: 'hidden', background: '#fbfbfb', border: '1px solid #e6e6e6' }}>
                                {kind === 'image' ? (
                                    <img
                                        src={preview}
                                        alt={`preview-${idx}`}
                                        style={{
                                            width: '100%',
                                            height: 100,
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : kind === 'video' ? (
                                    <div style={{ width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                                        <span style={{ color: '#fff', fontSize: 14 }}>
                                            🎬 Video
                                        </span>
                                    </div>
                                ) : kind === 'audio' ? (
                                    <div style={{ width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                                        <span style={{ color: '#444', fontSize: 14 }}>
                                            🎧 Audio
                                        </span>
                                    </div>
                                ) : (
                                    <div style={{ width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                                        <span style={{ color: '#444', fontSize: 14 }}>
                                            📄 File
                                        </span>
                                    </div>
                                )}
                                <div style={{ padding: 8, fontSize: 11, color: '#333', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {file.name}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeFile(idx)}
                                    style={{
                                        position: 'absolute',
                                        top: 6,
                                        right: 6,
                                        background: '#d32f2f',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: 20,
                                        height: 20,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px'
                                    }}
                                    title="Remove file"
                                >
                                    ✕
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <label style={{ cursor: 'pointer' }}>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/tiff,video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska,audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/aac,application/pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <span style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>
                        📎 Attach files
                    </span>
                </label>

                {/* Emoji button */}
                <div style={{ position: 'relative' }} ref={emojiMenuRef}>
                    <button
                        type="button"
                        onClick={() => setShowEmojis(!showEmojis)}
                        style={{
                            background: 'transparent',
                            border: '1px solid #ddd',
                            padding: '6px 10px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                        title="Add emoji"
                    >
                        😊
                    </button>

                    {showEmojis && (
                        <div style={styles.emojiMenu}>
                            {EMOJIS.map((item, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    style={styles.emojiButton}
                                    onClick={() => addEmoji(item.emoji)}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(0,0,0,0.05)'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                    title={item.label}
                                >
                                    {item.emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="fm-button"
                    onClick={handleSend}
                    disabled={sending || (!message.trim() && files.length === 0)}
                    style={{ padding: '8px 12px', marginLeft: 'auto' }}
                >
                    {sending ? 'Sending...' : 'Send'}
                </button>
            </div>
        </div>
    );
}

export default FormMessage;